import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CalendarDateInput, {
  isCalendarDateValue,
} from "../../common/dashboard/CalendarDateInput";
import EventTimeInput from "./CreateEvent/wizard/EventTimeInput";
import { eventDateTimeToUtcMilli } from "./eventDateTime";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";

export type EventScheduleSubmission = {
  publishAtUtcMilli: number;
  /** Internal review note; blank when the staff member left it empty. */
  note: string;
};

type EventScheduleDialogProps = {
  open: boolean;
  eventName: string;
  timeZone: string;
  saving?: boolean;
  /** Save or validation error from the caller (e.g. publication after start). */
  error?: string;
  onCancel: () => void;
  onConfirm: (submission: EventScheduleSubmission) => void;
};

const DEFAULT_PUBLISH_TIME = "10:00";

export const eventSchedulePastDateError = "Choose today or a later date.";

// Claude Design (4) "Schedule Event" pop-up (Create Event Screen CE:1546-1574,
// Manage Events Screen ME:309-337): date, time (10:00 AM default), and an
// internal review note, then "Schedule to Publish". Claude Design (19) Manage
// Events Screen.dc.html:341-370: 17px/600 title, DateField calendar that
// shows today while empty, #9E9E9E note border.
const EventScheduleDialog = ({
  open,
  eventName,
  timeZone,
  saving = false,
  error,
  onCancel,
  onConfirm,
}: EventScheduleDialogProps): React.ReactElement => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState(DEFAULT_PUBLISH_TIME);
  const [note, setNote] = useState("");
  const [dateError, setDateError] = useState("");

  // Each opening starts a fresh schedule.
  useEffect(() => {
    if (open) {
      setDate("");
      setTime(DEFAULT_PUBLISH_TIME);
      setNote("");
      setDateError("");
    }
  }, [open]);

  // IF10 D16: the calendar does not block past days; Save checks them.
  const earliestDate = () =>
    DateTime.now()
      .setZone(normalizeCmpTimeZone(timeZone).timeZone)
      .toFormat("yyyy-MM-dd");
  const confirm = () => {
    if (!date) {
      setDateError("Select a scheduled date.");
      return;
    }
    if (!isCalendarDateValue(date)) {
      setDateError("Select the Scheduled Date from the calendar.");
      return;
    }
    if (date < earliestDate()) {
      setDateError(eventSchedulePastDateError);
      return;
    }
    const publishAtUtcMilli = eventDateTimeToUtcMilli(date, time || DEFAULT_PUBLISH_TIME, timeZone);
    if (!Number.isFinite(publishAtUtcMilli) || publishAtUtcMilli <= Date.now()) {
      setDateError("Choose a date and time in the future.");
      return;
    }
    setDateError("");
    onConfirm({ publishAtUtcMilli, note: note.trim() });
  };

  const labelClassName = "text-[14px] font-semibold text-[#0A0A0A]";
  return (
    <Dialog
      open={open}
      onClose={() => { if (!saving) onCancel(); }}
      maxWidth={false}
      aria-labelledby="event-schedule-title"
      sx={{ zIndex: 3000 }}
      BackdropProps={{ sx: { backgroundColor: "rgba(10, 10, 10, 0.45)" } }}
      PaperProps={{
        sx: {
          border: "1px solid #ECECEC",
          borderRadius: "14px",
          boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          gap: "18px",
          margin: 0,
          maxWidth: "92vw",
          padding: "28px 30px",
          position: "relative",
          width: 480,
        },
      }}
    >
      <IconButton
        aria-label="Close"
        disabled={saving}
        onClick={onCancel}
        sx={{
          backgroundColor: "#F21B1B",
          color: "#FFFFFF",
          height: 28,
          padding: 0,
          position: "absolute",
          right: 14,
          top: 14,
          width: 28,
          "&:hover": { backgroundColor: "#D01414" },
        }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
      <div>
        <h2 id="event-schedule-title" className="text-[17px] font-semibold text-[#0A0A0A]">
          Schedule Event
        </h2>
        <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
          Choose a date and time to schedule <span className="font-semibold">{eventName || "this event"}</span> instead of publishing it right away.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-[8px]">
          <span className={labelClassName}>Scheduled Date</span>
          <CalendarDateInput
            mode="fullDate"
            appearance="claude"
            iconPosition="left"
            ariaLabel="Scheduled date"
            placeholder="Select scheduled date"
            highlightTodayWhenEmpty
            value={date}
            onChange={(value) => { setDate(value); setDateError(""); }}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-[8px]">
          <span className={labelClassName}>Scheduled Time</span>
          <EventTimeInput ariaLabel="Scheduled time" placeholder="Select Time" value={time} onChange={setTime} />
        </div>
      </div>
      <div className="flex flex-col gap-[8px]">
        <label htmlFor="event-schedule-note" className={labelClassName}>Internal Review Note</label>
        <textarea
          id="event-schedule-note"
          value={note}
          onChange={({ target }) => setNote(target.value)}
          maxLength={1000}
          placeholder="Add a note for review, schedule, or publish history."
          className="min-h-[80px] w-full resize-y rounded-[8px] border border-[#9E9E9E] px-[14px] py-[12px] text-[14px] text-[#0A0A0A] outline-none focus:border-cpaGold"
        />
      </div>
      {dateError || error ? (
        <p role="alert" className="text-[13px] text-[#F0202E]">{dateError || error}</p>
      ) : null}
      <div className="flex justify-end gap-[12px]">
        <button
          type="button"
          disabled={saving}
          onClick={onCancel}
          className="rounded-[8px] border border-[#ECECEC] bg-white px-[22px] py-[11px] text-[14px] font-semibold text-[#0A0A0A] hover:bg-[#F7F7F8] disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={confirm}
          className="rounded-[8px] border-0 bg-[#23272F] px-[22px] py-[11px] text-[14px] font-semibold text-white hover:bg-[#0A0A0A] disabled:opacity-50"
        >
          {saving ? "Scheduling..." : "Schedule to Publish"}
        </button>
      </div>
    </Dialog>
  );
};

export default EventScheduleDialog;
